import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import AIService from "./AIService";
import {
  AndroidListingSchema,
  AppStoreConnectPublishResponse,
  AppStoreConnectValidationResponse,
  AppStoreListing,
  CreateListingDTO,
  FetchAppStoreConnectWhatsNewResponse,
  GooglePlayPublishMode,
  GooglePlayPublishResponse,
  GooglePlayValidationResponse,
  ListingGenerationSkippedLocale,
  IOSListingSchema,
  ListingLocaleEntry,
  ListingPlatform,
  UpdateAppStoreConnectWhatsNewResponse,
} from "../types/listing";
import { buildAppContextLines } from "../utils/appContextPrompt";
import { getStoreLocaleOption } from "../utils/storeLocales";
import { auth } from "../firebase/config";

const LISTINGS_COLLECTION = "appStoreListings";
const PUBLISH_GOOGLE_PLAY_ENDPOINT =
  process.env.REACT_APP_PUBLISH_GOOGLE_PLAY_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/publishGooglePlayListing";
const VALIDATE_GOOGLE_PLAY_ENDPOINT =
  process.env.REACT_APP_VALIDATE_GOOGLE_PLAY_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/validateGooglePlayListing";
const PUBLISH_APP_STORE_CONNECT_ENDPOINT =
  process.env.REACT_APP_PUBLISH_APP_STORE_CONNECT_LISTING_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/publishAppStoreConnectListing";
const VALIDATE_APP_STORE_CONNECT_ENDPOINT =
  process.env.REACT_APP_VALIDATE_APP_STORE_CONNECT_LISTING_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/validateAppStoreConnectListing";
const FETCH_APP_STORE_CONNECT_WHATS_NEW_ENDPOINT =
  process.env.REACT_APP_FETCH_APP_STORE_CONNECT_WHATS_NEW_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/fetchAppStoreConnectWhatsNew";
const UPDATE_APP_STORE_CONNECT_WHATS_NEW_ENDPOINT =
  process.env.REACT_APP_UPDATE_APP_STORE_CONNECT_WHATS_NEW_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/updateAppStoreConnectWhatsNew";

const getIOSSchema = (includeWhatsNew: boolean) => ({
  type: "object",
  properties: {
    appName: { type: "string" },
    title: { type: "string", maxLength: 30 },
    subtitle: { type: "string", maxLength: 30 },
    promotionalText: { type: "string", maxLength: 170 },
    description: { type: "string", maxLength: 4000 },
    keywords: {
      type: "array",
      items: { type: "string" },
    },
    supportUrl: { type: "string" },
    marketingUrl: { type: "string" },
    ...(includeWhatsNew
      ? {
          whatsNew: { type: "string", maxLength: 4000 },
        }
      : {}),
  },
  required: [
    "appName",
    "title",
    "subtitle",
    "promotionalText",
    "description",
    "keywords",
    "supportUrl",
    "marketingUrl",
    ...(includeWhatsNew ? ["whatsNew"] : []),
  ],
});

const ANDROID_SCHEMA = {
  type: "object",
  properties: {
    appName: { type: "string" },
    title: { type: "string", maxLength: 30 },
    shortDescription: { type: "string", maxLength: 80 },
    fullDescription: { type: "string", maxLength: 4000 },
  },
  required: ["appName", "title", "shortDescription", "fullDescription"],
};

const INITIAL_GENERATION_MAX_RETRIES = 2;
const INITIAL_GENERATION_RETRY_MODEL = "gpt-4.1-mini";
type ListingBaseContentByPlatform = Partial<
  Record<ListingPlatform, IOSListingSchema | AndroidListingSchema>
>;

type ListingComplianceIssue = {
  field: string;
  actual: number;
  limit: number;
};

export class ListingService {
  private static convertTimestampToDate(value: any): Date {
    if (value instanceof Date) return value;
    if (value?.toDate && typeof value.toDate === "function") {
      return value.toDate();
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private static mapListingDocument(data: any): AppStoreListing {
    return {
      listingId: data.listingId,
      userId: data.userId,
      appId: data.appId || undefined,
      versionGroupId: data.versionGroupId || undefined,
      versionNumber:
        typeof data.versionNumber === "number" ? data.versionNumber : undefined,
      appName: data.appName,
      category: data.category,
      platform: data.platform,
      locales: Array.isArray(data.locales) ? data.locales : [],
      entries: Array.isArray(data.entries) ? data.entries : [],
      googlePlayPublishedAt: data.googlePlayPublishedAt
        ? this.convertTimestampToDate(data.googlePlayPublishedAt)
        : undefined,
      appStoreConnectPublishedAt: data.appStoreConnectPublishedAt
        ? this.convertTimestampToDate(data.appStoreConnectPublishedAt)
        : undefined,
      createdAt: data.createdAt
        ? this.convertTimestampToDate(data.createdAt)
        : new Date(),
      updatedAt: data.updatedAt
        ? this.convertTimestampToDate(data.updatedAt)
        : new Date(),
    } as AppStoreListing;
  }

  static async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      const error = new Error(
        detailedMessage || `Request failed with status ${response.status}`,
      ) as Error & { responseData?: unknown };
      error.responseData = responseData;
      throw error;
    }

    return responseData as T;
  }

  static async generateListing(
    userId: string,
    appId: string | undefined,
    data: CreateListingDTO,
    options?: {
      approvedBaseContentByPlatform?: ListingBaseContentByPlatform;
    },
  ): Promise<AppStoreListing[]> {
    try {
      const platforms: ListingPlatform[] =
        data.platform === "both" ? ["ios", "android"] : [data.platform];

      const createdListings: AppStoreListing[] = [];
      const existingAppListings =
        appId && userId ? await this.getAppListings(appId, userId) : [];
      const skippedLocales: ListingGenerationSkippedLocale[] = [];

      for (const platform of platforms) {
        const entries: ListingLocaleEntry[] = [];
        const approvedBaseRaw =
          options?.approvedBaseContentByPlatform?.[platform];
        const approvedBaseContent = approvedBaseRaw
          ? (this.sanitizeListingContent(
              this.appendLegalLinks(
                approvedBaseRaw as IOSListingSchema | AndroidListingSchema,
                platform,
                data.privacyPolicyUrl,
                data.termsOfServiceUrl,
              ),
              platform,
              { enforceMaxLengths: false },
            ) as IOSListingSchema | AndroidListingSchema)
          : null;

        for (const locale of data.locales) {
          try {
            let sanitized: IOSListingSchema | AndroidListingSchema;
            if (approvedBaseContent && this.isEnglishLocale(locale)) {
              sanitized = this.sanitizeListingContent(
                approvedBaseContent,
                platform,
                {
                  enforceMaxLengths: false,
                },
              );
            } else {
              const schema =
                platform === "ios"
                  ? getIOSSchema(Boolean(data.includeWhatsNew))
                  : ANDROID_SCHEMA;
              const structured = await this.generateCompliantListingContent(
                data,
                platform,
                locale,
                schema,
                {
                  baseEnglishContent: approvedBaseContent || undefined,
                },
              );
              sanitized = this.sanitizeListingContent(
                structured,
                platform,
                {
                  enforceMaxLengths: false,
                },
              );
            }

            entries.push({
              locale,
              content: sanitized,
            });
          } catch (error) {
            const message =
              error instanceof Error && error.message
                ? error.message
                : "Locale generation failed.";
            console.error(
              `Skipping failed ${platform} listing locale ${locale}:`,
              error,
            );
            skippedLocales.push({
              locale,
              platform,
              message,
            });
          }
        }

        if (entries.length === 0) {
          console.warn(
            `No ${platform} listing locales were generated successfully. Skipping ${platform} listing creation.`,
          );
          continue;
        }

        const listingId = doc(collection(db, LISTINGS_COLLECTION)).id;
        const matchingPlatformListings = existingAppListings.filter(
          (listing) => listing.platform === platform,
        );
        const nextVersionNumber =
          matchingPlatformListings.reduce(
            (maxVersion, listing) =>
              Math.max(maxVersion, Number(listing.versionNumber || 0)),
            0,
          ) + 1;
        const listing: AppStoreListing = {
          listingId,
          userId,
          appId,
          versionGroupId: appId ? `${appId}_${platform}` : undefined,
          versionNumber: appId ? nextVersionNumber : undefined,
          appName: data.appName,
          category: data.category,
          platform,
          locales: entries.map((entry) => entry.locale),
          entries,
          ...(skippedLocales.length > 0
            ? {
                generationSkippedLocales: skippedLocales.filter(
                  (item) => item.platform === platform,
                ),
              }
            : {}),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        await setDoc(doc(db, LISTINGS_COLLECTION, listingId), listing);
        createdListings.push(
          this.mapListingDocument({
            ...listing,
            createdAt: new Date(),
            updatedAt: new Date(),
          }),
        );
      }

      if (createdListings.length === 0) {
        const skipSummary = skippedLocales
          .map(
            (item) =>
              `${item.platform.toUpperCase()} ${item.locale}: ${item.message}`,
          )
          .join(" | ");
        throw new Error(
          skipSummary
            ? `Listing generation failed for all selected locales. ${skipSummary}`
            : "Listing generation failed for all selected locales.",
        );
      }

      return createdListings;
    } catch (error) {
      console.error("Error generating listing:", error);
      throw error;
    }
  }

  static async generateBaseEnglishListingDraft(
    data: CreateListingDTO,
    platform: ListingPlatform,
  ): Promise<IOSListingSchema | AndroidListingSchema> {
    const schema =
      platform === "ios"
        ? getIOSSchema(Boolean(data.includeWhatsNew))
        : ANDROID_SCHEMA;
    const baseContent = await this.generateCompliantListingContent(
      data,
      platform,
      "en-US",
      schema,
    );

    return this.sanitizeListingContent(baseContent, platform, {
      enforceMaxLengths: false,
    });
  }

  static async getUserListings(userId: string): Promise<AppStoreListing[]> {
    try {
      const q = query(
        collection(db, LISTINGS_COLLECTION),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) =>
        this.mapListingDocument(doc.data()),
      );
    } catch (error) {
      console.error("Error getting listings:", error);
      throw error;
    }
  }

  static async getListingById(
    listingId: string,
  ): Promise<AppStoreListing | null> {
    try {
      const docSnap = await getDoc(doc(db, LISTINGS_COLLECTION, listingId));
      return docSnap.exists() ? this.mapListingDocument(docSnap.data()) : null;
    } catch (error) {
      console.error("Error getting listing:", error);
      throw error;
    }
  }

  static async getAppListings(
    appId: string,
    userId: string,
  ): Promise<AppStoreListing[]> {
    try {
      const q = query(
        collection(db, LISTINGS_COLLECTION),
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) =>
        this.mapListingDocument(doc.data()),
      );
    } catch (error) {
      console.error("Error getting app listings:", error);
      throw error;
    }
  }

  static async createIOSWhatsNewSnapshot(params: {
    userId: string;
    appId: string;
    appName: string;
    category: string;
    whatsNewByLocale: Record<string, string>;
  }): Promise<AppStoreListing> {
    const appListings = await this.getAppListings(params.appId, params.userId);
    const iosListings = appListings.filter(
      (listing) => listing.platform === "ios",
    );

    if (!iosListings.length) {
      throw new Error(
        "No existing iOS listing was found for this app. Generate one first, then use What's New updater.",
      );
    }

    const sourceListing = [...iosListings].sort((a, b) => {
      const versionA = Number(a.versionNumber || 0);
      const versionB = Number(b.versionNumber || 0);
      if (versionA !== versionB) return versionB - versionA;
      return (
        new Date(b.updatedAt as any).getTime() -
        new Date(a.updatedAt as any).getTime()
      );
    })[0];

    const listingId = doc(collection(db, LISTINGS_COLLECTION)).id;
    const nextVersionNumber =
      iosListings.reduce(
        (maxVersion, listing) =>
          Math.max(maxVersion, Number(listing.versionNumber || 0)),
        0,
      ) + 1;

    const updatedEntries = (
      Array.isArray(sourceListing.entries) ? sourceListing.entries : []
    ).map((entry) => {
      const locale = String(entry?.locale || "").trim();
      const nextWhatsNew =
        typeof params.whatsNewByLocale[locale] === "string"
          ? params.whatsNewByLocale[locale]
          : undefined;

      const existingContent = (entry.content || {}) as IOSListingSchema;
      const mergedContent = {
        ...existingContent,
        ...(typeof nextWhatsNew === "string" ? { whatsNew: nextWhatsNew } : {}),
      };

      return {
        locale,
        content: this.sanitizeListingContent(mergedContent, "ios", {
          enforceMaxLengths: false,
        }) as IOSListingSchema,
      };
    });

    const snapshot: AppStoreListing = {
      listingId,
      userId: params.userId,
      appId: params.appId,
      versionGroupId: `${params.appId}_ios`,
      versionNumber: nextVersionNumber,
      appName: params.appName,
      category: params.category,
      platform: "ios",
      locales: updatedEntries.map((entry) => entry.locale),
      entries: updatedEntries,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, LISTINGS_COLLECTION, listingId), snapshot);

    return this.mapListingDocument({
      ...snapshot,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  }

  static async deleteListing(listingId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, LISTINGS_COLLECTION, listingId));
    } catch (error) {
      console.error("Error deleting listing:", error);
      throw error;
    }
  }

  static sanitizeListingContentForPlatform(
    content: IOSListingSchema | AndroidListingSchema,
    platform: ListingPlatform,
    options?: {
      enforceMaxLengths?: boolean;
    },
  ): IOSListingSchema | AndroidListingSchema {
    return this.sanitizeListingContent(content, platform, options);
  }

  static async updateListingEntryContent(params: {
    listingId: string;
    locale: string;
    platform: ListingPlatform;
    content: IOSListingSchema | AndroidListingSchema;
  }): Promise<AppStoreListing> {
    const listingRef = doc(db, LISTINGS_COLLECTION, params.listingId);
    const listingSnap = await getDoc(listingRef);

    if (!listingSnap.exists()) {
      throw new Error("Listing not found");
    }

    const listing = listingSnap.data() as AppStoreListing;
    const sanitizedContent = this.sanitizeListingContent(
      params.content,
      params.platform,
      { enforceMaxLengths: false },
    );
    const existingEntries = Array.isArray(listing.entries)
      ? listing.entries
      : [];
    const updatedEntries = existingEntries.map((entry) =>
      entry.locale === params.locale
        ? {
            ...entry,
            content: sanitizedContent,
          }
        : entry,
    );

    await updateDoc(listingRef, {
      entries: updatedEntries,
      updatedAt: serverTimestamp(),
    });

    return this.mapListingDocument({
      ...listing,
      entries: updatedEntries,
      updatedAt: new Date(),
    });
  }

  static async markListingPublished(params: {
    listingId: string;
    publishedAt?: Date;
    platform?: ListingPlatform;
  }): Promise<AppStoreListing> {
    const listingRef = doc(db, LISTINGS_COLLECTION, params.listingId);
    const listingSnap = await getDoc(listingRef);

    if (!listingSnap.exists()) {
      throw new Error("Listing not found");
    }

    const listing = this.mapListingDocument(listingSnap.data());
    const publishedAt = params.publishedAt || new Date();
    const publishField =
      params.platform === "ios"
        ? "appStoreConnectPublishedAt"
        : "googlePlayPublishedAt";

    await updateDoc(listingRef, {
      [publishField]: publishedAt,
      updatedAt: serverTimestamp(),
    });

    return this.mapListingDocument({
      ...listing,
      [publishField]: publishedAt,
      updatedAt: new Date(),
    });
  }

  static async publishGooglePlayListing(params: {
    appId: string;
    listingId: string;
    mode: GooglePlayPublishMode;
    locale?: string;
    locales?: string[];
  }): Promise<GooglePlayPublishResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayPublishResponse>(
        PUBLISH_GOOGLE_PLAY_ENDPOINT,
        {
          appId: params.appId,
          listingId: params.listingId,
          mode: params.mode,
          ...(params.locale ? { locale: params.locale } : {}),
          ...(params.locales?.length ? { locales: params.locales } : {}),
        },
      );

    return (
      responseData || {
        success: true,
        packageName: "",
        attemptedLocales: [],
        results: [],
        commitStatus: "not_committed",
      }
    );
  }

  static async validateGooglePlayListing(params: {
    appId: string;
    listingId: string;
    mode: GooglePlayPublishMode;
    locale?: string;
    locales?: string[];
  }): Promise<GooglePlayValidationResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayValidationResponse>(
        VALIDATE_GOOGLE_PLAY_ENDPOINT,
        {
          appId: params.appId,
          listingId: params.listingId,
          mode: params.mode,
          ...(params.locale ? { locale: params.locale } : {}),
          ...(params.locales?.length ? { locales: params.locales } : {}),
        },
      );

    return (
      responseData || {
        success: true,
        packageName: "",
        attemptedLocales: [],
        results: [],
      }
    );
  }

  static async publishAppStoreConnectListing(params: {
    appId: string;
    listingId: string;
    mode: GooglePlayPublishMode;
    locale?: string;
    locales?: string[];
  }): Promise<AppStoreConnectPublishResponse> {
    const responseData =
      await this.postAuthenticatedJson<AppStoreConnectPublishResponse>(
        PUBLISH_APP_STORE_CONNECT_ENDPOINT,
        {
          appId: params.appId,
          listingId: params.listingId,
          mode: params.mode,
          ...(params.locale ? { locale: params.locale } : {}),
          ...(params.locales?.length ? { locales: params.locales } : {}),
        },
      );

    return (
      responseData || {
        success: true,
        packageName: "",
        attemptedLocales: [],
        results: [],
        commitStatus: "not_committed",
      }
    );
  }

  static async validateAppStoreConnectListing(params: {
    appId: string;
    listingId: string;
    mode: GooglePlayPublishMode;
    locale?: string;
    locales?: string[];
  }): Promise<AppStoreConnectValidationResponse> {
    const responseData =
      await this.postAuthenticatedJson<AppStoreConnectValidationResponse>(
        VALIDATE_APP_STORE_CONNECT_ENDPOINT,
        {
          appId: params.appId,
          listingId: params.listingId,
          mode: params.mode,
          ...(params.locale ? { locale: params.locale } : {}),
          ...(params.locales?.length ? { locales: params.locales } : {}),
        },
      );

    return (
      responseData || {
        success: true,
        packageName: "",
        attemptedLocales: [],
        results: [],
      }
    );
  }

  static async fetchAppStoreConnectWhatsNew(params: {
    appId: string;
  }): Promise<FetchAppStoreConnectWhatsNewResponse> {
    const responseData =
      await this.postAuthenticatedJson<FetchAppStoreConnectWhatsNewResponse>(
        FETCH_APP_STORE_CONNECT_WHATS_NEW_ENDPOINT,
        { appId: params.appId },
      );
    return responseData || { success: true, locales: [] };
  }

  static async updateAppStoreConnectWhatsNew(params: {
    appId: string;
    whatsNewByLocale: Record<string, string>;
  }): Promise<UpdateAppStoreConnectWhatsNewResponse> {
    const responseData =
      await this.postAuthenticatedJson<UpdateAppStoreConnectWhatsNewResponse>(
        UPDATE_APP_STORE_CONNECT_WHATS_NEW_ENDPOINT,
        {
          appId: params.appId,
          whatsNewByLocale: params.whatsNewByLocale,
        },
      );
    return responseData || { success: true, results: [] };
  }

  static exportAllEntriesAsJSON(listing: AppStoreListing): string {
    return JSON.stringify(listing.entries, null, 2);
  }

  static exportEntryAsText(listing: AppStoreListing, locale: string): string {
    const entry = listing.entries.find((item) => item.locale === locale);
    if (!entry) return "";
    return JSON.stringify(entry.content, null, 2);
  }

  private static async generateCompliantListingContent(
    data: CreateListingDTO,
    platform: ListingPlatform,
    locale: string,
    schema: ReturnType<typeof getIOSSchema> | typeof ANDROID_SCHEMA,
    options?: {
      baseEnglishContent?: IOSListingSchema | AndroidListingSchema;
    },
  ): Promise<IOSListingSchema | AndroidListingSchema> {
    const primaryModel = data.model || AIService.getDefaultModel();
    let lastStructured: IOSListingSchema | AndroidListingSchema | null = null;
    let previousIssues: ListingComplianceIssue[] = [];
    let previousFailureMessage = "";

    for (
      let attempt = 1;
      attempt <= INITIAL_GENERATION_MAX_RETRIES + 1;
      attempt += 1
    ) {
      const modelForAttempt =
        attempt === 1 ? primaryModel : INITIAL_GENERATION_RETRY_MODEL;
      const prompt =
        options?.baseEnglishContent && !this.isEnglishLocale(locale)
          ? this.buildListingLocalizationPromptFromBase(
              data,
              platform,
              locale,
              options.baseEnglishContent,
              {
                attempt,
                previousIssues,
                previousFailureMessage,
              },
            )
          : this.buildListingPrompt(data, platform, locale, {
              attempt,
              previousIssues,
              previousFailureMessage,
            });
      let structuredRaw: IOSListingSchema | AndroidListingSchema;
      try {
        structuredRaw = await AIService.generateJSON<
          IOSListingSchema | AndroidListingSchema
        >(prompt, {
          model: modelForAttempt,
          structuredOutputSchema: schema,
          structuredOutputName: `${platform}_app_store_listing`,
          temperature: 0.2,
          maxTokens: 4096,
        });
      } catch (error) {
        previousFailureMessage =
          error instanceof Error && error.message
            ? error.message
            : "Listing generation failed.";
        if (attempt > INITIAL_GENERATION_MAX_RETRIES) {
          throw error;
        }
        continue;
      }
      const withLegalLinks = this.appendLegalLinks(
        structuredRaw,
        platform,
        data.privacyPolicyUrl,
        data.termsOfServiceUrl,
      );
      const sanitized = this.sanitizeListingContent(withLegalLinks, platform, {
        enforceMaxLengths: false,
      });
      const issues = this.getListingContentComplianceIssues(
        sanitized,
        platform,
      );

      lastStructured = sanitized;
      previousIssues = issues;
      previousFailureMessage = "";

      if (issues.length === 0) {
        return sanitized;
      }
    }

    return lastStructured as IOSListingSchema | AndroidListingSchema;
  }

  private static getListingContentComplianceIssues(
    content: IOSListingSchema | AndroidListingSchema,
    platform: ListingPlatform,
  ): ListingComplianceIssue[] {
    if (platform === "ios") {
      const ios = content as IOSListingSchema;
      const lengthIssues = [
        {
          field: "title",
          actual: String(ios.title || "").trim().length,
          limit: 30,
        },
        {
          field: "subtitle",
          actual: String(ios.subtitle || "").trim().length,
          limit: 30,
        },
        {
          field: "promotionalText",
          actual: String(ios.promotionalText || "").trim().length,
          limit: 170,
        },
        {
          field: "description",
          actual: String(ios.description || "").trim().length,
          limit: 4000,
        },
        {
          field: "keywords",
          actual: Array.isArray(ios.keywords)
            ? ios.keywords
                .map((keyword) => String(keyword || "").trim())
                .filter(Boolean)
                .join(",").length
            : 0,
          limit: 100,
        },
        {
          field: "whatsNew",
          actual: String(ios.whatsNew || "").trim().length,
          limit: 4000,
        },
      ].filter((issue) => issue.actual > issue.limit);
      const requiredFieldIssues: ListingComplianceIssue[] = [];
      if (!String(ios.supportUrl || "").trim()) {
        requiredFieldIssues.push({
          field: "supportUrl",
          actual: 0,
          limit: 1,
        });
      }
      if (!String(ios.marketingUrl || "").trim()) {
        requiredFieldIssues.push({
          field: "marketingUrl",
          actual: 0,
          limit: 1,
        });
      }
      return [...lengthIssues, ...requiredFieldIssues];
    }

    const android = content as AndroidListingSchema;
    return [
      {
        field: "title",
        actual: String(android.title || "").trim().length,
        limit: 30,
      },
      {
        field: "shortDescription",
        actual: String(android.shortDescription || "").trim().length,
        limit: 80,
      },
      {
        field: "fullDescription",
        actual: String(android.fullDescription || "").trim().length,
        limit: 4000,
      },
    ].filter((issue) => issue.actual > issue.limit);
  }

  private static buildListingPrompt(
    data: CreateListingDTO,
    platform: ListingPlatform,
    locale: string,
    options?: {
      attempt?: number;
      previousIssues?: ListingComplianceIssue[];
      previousFailureMessage?: string;
    },
  ): string {
    const localeOption = getStoreLocaleOption(locale);
    const localeLabel = localeOption?.name || locale;
    const previousIssues = options?.previousIssues || [];
    const promptLines = [
      `Generate a high-converting ${
        platform === "ios" ? "Apple App Store" : "Google Play"
      } listing.`,
      `Locale: ${locale}`,
      `Target language: ${localeLabel}`,
      ...buildAppContextLines({
        name: data.appName,
        description: data.appDescription,
        category: data.category,
        platform: data.appPlatform,
        storeUrl: data.storeUrl,
        androidStoreUrl: data.androidStoreUrl,
        iosStoreUrl: data.iosStoreUrl,
        researchContext: data.researchContext,
      }),
      `Target audience: ${data.targetAudience || "General mobile users"}`,
      `Core features: ${data.features.join(", ") || "N/A"}`,
      `Unique selling points: ${data.usps.join(", ") || "N/A"}`,
      `Seed keywords: ${data.keywords.join(", ") || "N/A"}`,
      `Privacy Policy URL: ${data.privacyPolicyUrl || "N/A"}`,
      `Terms of Service URL: ${data.termsOfServiceUrl || "N/A"}`,
      `Support URL base: ${data.supportUrl || "N/A"}`,
      `Marketing URL base: ${data.marketingUrl || "N/A"}`,
      "",
      `Keep language natural for ${localeLabel} and optimize for conversion and ASO.`,
      `Write every generated field fully in ${localeLabel}. Do not fall back to English unless the target locale itself is English.`,
      "Translate and localize the copy for native speakers of the target language rather than leaving source text untranslated.",
      "Policy safety is mandatory: produce copy that is compliant with both Apple App Store Review Guidelines and Google Play Developer Program Policies.",
      "Do not include disallowed, risky, or unverifiable claims. Use conservative, factual wording when uncertain.",
      "Never claim guaranteed outcomes, medical/health cures, legal/financial certainty, or absolute safety/security.",
      "Do not include deceptive urgency, fake scarcity, misleading pricing, or promotional claims that cannot be verified from the provided app context.",
      "Do not mention competitor names, ranking claims (for example #1/best/top), or awards unless explicitly provided in the app context.",
      "Do not include prohibited or sensitive content references (sexual content, hate, violence, illegal drugs, weapons, gambling) unless clearly required by the app context and platform rules.",
      "Do not instruct users to bypass platform billing, moderation, safety systems, or account controls.",
      "Do not request or imply collection of sensitive personal data in listing copy.",
      "For iOS output, include valid `supportUrl` and `marketingUrl` values.",
      "URLs must start with https:// and contain no spaces.",
      "If locale-specific URLs are not available, reuse the provided base URLs unchanged.",
      "Treat every store field limit as a hard requirement, not a suggestion.",
      "Do not return text that exceeds the allowed length for any field.",
      "Aim slightly under the maximum length where possible so the result stays safely compliant.",
      "ASO keyword strategy is mandatory.",
      "Use the provided seed keywords as core intents and derive natural long-tail keyword variations from them.",
      "Integrate both core and long-tail variants into meaningful, user-facing sentences across visible fields (for iOS: subtitle/promotionalText/description; for Android: shortDescription/fullDescription).",
      "Keep keyword usage natural and readable. Do not keyword-stuff, avoid repetitive exact-match spam, and do not output raw keyword lists in description fields.",
      platform === "ios"
        ? "Hard limits: title 30 chars, subtitle 30 chars, promotionalText 170 chars, description 4000 chars, keywords max 100 total characters including commas, What's New 4000 chars."
        : "Hard limits: title 30 chars, shortDescription 80 chars, fullDescription 4000 chars.",
      platform === "ios"
        ? "Apple-specific: avoid references that can trigger rejection such as unsupported functionality claims, misleading metadata, or prohibited content positioning."
        : "Google Play-specific: avoid policy-risk wording around deceptive behavior, sensitive events, restricted content, and misleading metadata.",
      "Generate the app store listing using only standard UTF-8 plain text.",
      "Strictly follow these formatting constraints:",
      "- No emojis or decorative symbols.",
      "- Use only standard keyboard punctuation and straight quotes.",
      "- Use a simple hyphen for bullets instead of typographic bullets or dashes.",
      "- Do not use curly quotes, em dashes, en dashes, ellipses, or mathematical symbols.",
      "- Do not include HTML, rich text, non-breaking spaces, or hidden formatting characters.",
      "- Use clean single or double line breaks only.",
      "Include privacy policy and terms links in the final section of the long description content.",
    ];

    if (platform === "ios") {
      if (data.includeWhatsNew) {
        const whatsNewBase = String(data.whatsNewBaseText || "").trim();
        promptLines.push(
          "Include a localized `whatsNew` field for this locale.",
          "Use the base update note below as source intent, then rewrite naturally in the target locale while preserving meaning.",
          `What's New base text: ${whatsNewBase || "Bug fixes and performance improvements."}`,
          "Keep `whatsNew` clear, factual, and policy-safe. Avoid vague hype and unsupported claims.",
        );
      } else {
        promptLines.push(
          "Do not include a `whatsNew` field because this generation excludes release notes.",
        );
      }
    }

    const listingContextBase = String(data.listingContextBaseText || "").trim();
    if (listingContextBase) {
      promptLines.push(
        "",
        "Append one localized contextual note at the end of the long description content for this locale.",
        "For iOS, append it at the end of `description`. For Android, append it at the end of `fullDescription`.",
        "Localize the note naturally for the target locale while preserving legal/compliance meaning.",
        "Keep it concise, policy-safe, and clearly separated as a final note section.",
        `Context note base text: ${listingContextBase}`,
      );
    }

    if (options?.previousFailureMessage) {
      promptLines.push(
        "",
        `Previous generation attempt failed with this error: ${options.previousFailureMessage}`,
        `Retry using a stricter JSON-safe response and fully valid ${platform === "ios" ? "App Store" : "Google Play"} field output.`,
      );
    }

    if ((options?.attempt || 1) > 1 && previousIssues.length > 0) {
      promptLines.push(
        "",
        `Retry attempt ${options?.attempt}. The previous response failed compliance.`,
        ...previousIssues.map(
          (issue) =>
            `Previous issue: ${issue.field} came back at ${issue.actual}/${issue.limit}. Rewrite it shorter so it fully fits the limit.`,
        ),
        "If needed, rewrite failing fields from scratch instead of trying to preserve the previous wording.",
        "Remove filler words first, keep the meaning intact, and maintain strong conversion intent.",
      );
    }

    if (platform === "ios") {
      promptLines.splice(
        promptLines.length - 2,
        0,
        "- Keep keywords plain text only. Do not use plus signs or brackets inside keywords. Write phrases like LGBTQ plus instead of LGBTQ+.",
      );
    }

    return promptLines.join("\n");
  }

  private static buildListingLocalizationPromptFromBase(
    data: CreateListingDTO,
    platform: ListingPlatform,
    locale: string,
    baseEnglishContent: IOSListingSchema | AndroidListingSchema,
    options?: {
      attempt?: number;
      previousIssues?: ListingComplianceIssue[];
      previousFailureMessage?: string;
    },
  ): string {
    const localeOption = getStoreLocaleOption(locale);
    const localeLabel = localeOption?.name || locale;
    const previousIssues = options?.previousIssues || [];
    const baseJson = JSON.stringify(baseEnglishContent, null, 2);
    const promptLines = [
      `Localize an approved English ${
        platform === "ios" ? "Apple App Store" : "Google Play"
      } listing into a target locale.`,
      `Target locale: ${locale}`,
      `Target language: ${localeLabel}`,
      "",
      "Use this approved English base JSON as the only source of truth:",
      baseJson,
      "",
      "Requirements:",
      "- Preserve meaning and intent from the English base copy.",
      "- Preserve ASO intent from the base copy, including natural usage of core keywords and long-tail keyword variants.",
      "- Keep all URLs valid and unchanged unless locale-specific URL variants are explicitly available.",
      "- Keep output fully localized for native speakers in the target locale.",
      "- Do not add claims, features, or policy-sensitive statements not present in the English base.",
      "- Keep output plain UTF-8 text with no emojis, smart punctuation, or rich text.",
      "- Keep the same schema keys and value types as the expected output.",
      "- Treat every field limit as a hard requirement.",
      platform === "ios"
        ? "- Hard limits: title 30 chars, subtitle 30 chars, promotionalText 170 chars, description 4000 chars, keywords max 100 total characters including commas, What's New 4000 chars."
        : "- Hard limits: title 30 chars, shortDescription 80 chars, fullDescription 4000 chars.",
      "Policy safety is mandatory for App Store and Google Play.",
      "Use conservative, factual wording. Avoid unsupported guarantees or absolute claims.",
    ];

    if (platform === "ios") {
      promptLines.push(
        "For iOS, keep `keywords` as an array of short localized plain-text terms.",
        "Do not include symbols like + in keywords.",
      );
      if (data.includeWhatsNew) {
        promptLines.push(
          "If `whatsNew` exists in the base JSON, localize it naturally while preserving release-note intent.",
        );
      }
    }

    if (options?.previousFailureMessage) {
      promptLines.push(
        "",
        `Previous generation attempt failed with this error: ${options.previousFailureMessage}`,
        "Retry with stricter JSON-safe output.",
      );
    }

    if ((options?.attempt || 1) > 1 && previousIssues.length > 0) {
      promptLines.push(
        "",
        `Retry attempt ${options?.attempt}. The previous response failed compliance.`,
        ...previousIssues.map(
          (issue) =>
            `Previous issue: ${issue.field} came back at ${issue.actual}/${issue.limit}. Rewrite it shorter so it fully fits the limit.`,
        ),
        "If needed, rewrite failing fields from scratch while keeping source meaning.",
      );
    }

    return promptLines.join("\n");
  }

  private static isEnglishLocale(locale: string): boolean {
    return /^en(?:-|$)/i.test(String(locale || "").trim());
  }

  private static sanitizeListingContent(
    content: IOSListingSchema | AndroidListingSchema,
    platform: ListingPlatform,
    options?: {
      enforceMaxLengths?: boolean;
    },
  ): IOSListingSchema | AndroidListingSchema {
    const trimIfNeeded = (value: string, max: number) =>
      options?.enforceMaxLengths === false
        ? String(value || "").trim()
        : this.trimToMaxChars(value, max);

    if (platform === "ios") {
      const ios = content as IOSListingSchema;
      return {
        ...ios,
        appName: this.sanitizePlainTextField(ios.appName),
        title: trimIfNeeded(this.sanitizePlainTextField(ios.title), 30),
        subtitle: trimIfNeeded(this.sanitizePlainTextField(ios.subtitle), 30),
        promotionalText: trimIfNeeded(
          this.sanitizePlainTextField(ios.promotionalText),
          170,
        ),
        description: trimIfNeeded(
          this.sanitizePlainTextBlock(ios.description),
          4000,
        ),
        keywords: this.sanitizeKeywords(ios.keywords),
        supportUrl: this.sanitizeUrlField(ios.supportUrl),
        marketingUrl: this.sanitizeUrlField(ios.marketingUrl),
        ...(typeof ios.whatsNew === "string"
          ? {
              whatsNew: trimIfNeeded(
                this.sanitizePlainTextBlock(ios.whatsNew),
                4000,
              ),
            }
          : {}),
      };
    }

    const android = content as AndroidListingSchema & { keywords?: string[] };
    return {
      appName: this.sanitizePlainTextField(android.appName),
      title: trimIfNeeded(this.sanitizePlainTextField(android.title), 30),
      shortDescription: trimIfNeeded(
        this.sanitizePlainTextField(android.shortDescription),
        80,
      ),
      fullDescription: trimIfNeeded(
        this.sanitizePlainTextBlock(android.fullDescription),
        4000,
      ),
    };
  }

  private static sanitizeKeywords(keywords: string[]): string[] {
    const sanitized = (Array.isArray(keywords) ? keywords : [])
      .map((keyword) => this.sanitizeKeyword(keyword))
      .filter(Boolean);

    const result: string[] = [];
    let currentLength = 0;
    for (const keyword of sanitized) {
      const nextLength =
        currentLength === 0
          ? keyword.length
          : currentLength + 1 + keyword.length;
      if (nextLength > 100) {
        break;
      }
      result.push(keyword);
      currentLength = nextLength;
    }

    return result;
  }

  private static sanitizeKeyword(value: string): string {
    return this.sanitizePlainTextField(value)
      .replace(/\+/g, " plus ")
      .replace(/[{}()<>]/g, " ")
      .replaceAll("[", " ")
      .replaceAll("]", " ")
      .replace(/\s+/g, " ")
      .replace(/\s*,\s*/g, ", ")
      .trim();
  }

  private static sanitizeUrlField(value: string | undefined): string {
    return String(value || "")
      .trim()
      .replace(/\s+/g, "");
  }

  private static sanitizePlainTextField(value: string): string {
    return this.sanitizePlainText(value, false);
  }

  private static sanitizePlainTextBlock(value: string): string {
    return this.sanitizePlainText(value, true);
  }

  private static sanitizePlainText(
    value: string,
    allowLineBreaks: boolean,
  ): string {
    let sanitized = String(value || "")
      .replace(/\r\n?/g, "\n")
      .replace(/[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]/g, " ")
      .replace(/[\u200C\u200D\u2060\uFE0E\uFE0F\uFEFF]/g, "")
      .replace(/[“”«»„‟]/g, '"')
      .replace(/[‘’‚‛‹›]/g, "'")
      .replace(/[‐‑‒–—―]/g, "-")
      .replace(/[•◦▪▫●○◆◇▶▷]/g, "-")
      .replace(/…/g, "...")
      .replace(/[×÷≈≠≤≥±√∑∏∞]/g, " ")
      .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, "")
      .replace(/\t/g, " ");

    if (!allowLineBreaks) {
      sanitized = sanitized.replace(/\n+/g, " ");
    } else {
      sanitized = sanitized.replace(/ *\n */g, "\n").replace(/\n{3,}/g, "\n\n");
    }

    return sanitized.replace(/[ ]{2,}/g, " ").trim();
  }

  private static appendLegalLinks(
    content: IOSListingSchema | AndroidListingSchema,
    platform: ListingPlatform,
    privacyPolicyUrl?: string,
    termsOfServiceUrl?: string,
  ): IOSListingSchema | AndroidListingSchema {
    const privacy = String(privacyPolicyUrl || "").trim();
    const terms = String(termsOfServiceUrl || "").trim();
    if (!privacy && !terms) return content;

    if (platform === "ios") {
      const ios = content as IOSListingSchema;
      const footerLines = this.getMissingLegalFooterLines(
        ios.description || "",
        {
          privacy,
          terms,
        },
      );
      if (footerLines.length === 0) {
        return ios;
      }
      const normalizedDescription = this.normalizeDescriptionLegalSection(
        ios.description || "",
        footerLines,
      );
      return {
        ...ios,
        description: normalizedDescription,
      };
    }

    const android = content as AndroidListingSchema;
    const footerLines = this.getMissingLegalFooterLines(
      android.fullDescription || "",
      {
        privacy,
        terms,
      },
    );
    if (footerLines.length === 0) {
      return android;
    }
    const normalizedDescription = this.normalizeDescriptionLegalSection(
      android.fullDescription || "",
      footerLines,
    );
    return {
      ...android,
      fullDescription: normalizedDescription,
    };
  }

  private static getMissingLegalFooterLines(
    value: string,
    urls: { privacy?: string; terms?: string },
  ): string[] {
    const normalized = String(value || "").toLowerCase();
    const footerLines: string[] = [];

    if (urls.privacy && !normalized.includes(urls.privacy.toLowerCase())) {
      footerLines.push(`Privacy Policy: ${urls.privacy}`);
    }

    if (urls.terms && !normalized.includes(urls.terms.toLowerCase())) {
      footerLines.push(`Terms of Service: ${urls.terms}`);
    }

    return footerLines;
  }

  private static normalizeDescriptionLegalSection(
    value: string,
    footerLines: string[],
  ): string {
    const normalized = String(value || "")
      .replace(/\r\n/g, "\n")
      .trim();
    if (!normalized) {
      return footerLines.join("\n");
    }

    const footerLookup = new Map(
      footerLines.map((line) => [line.trim().toLowerCase(), line.trim()]),
    );
    const seenFooterKeys = new Set<string>();
    const dedupedLines = normalized
      .split("\n")
      .map((line) => line.trimEnd())
      .filter((line, index, lines) => {
        const normalizedLine = line.trim().toLowerCase();
        if (!footerLookup.has(normalizedLine)) {
          return true;
        }
        if (seenFooterKeys.has(normalizedLine)) {
          return false;
        }
        seenFooterKeys.add(normalizedLine);
        return true;
      });

    const dedupedContent = dedupedLines.join("\n").trim();
    const missingFooterLines = footerLines.filter(
      (line) => !seenFooterKeys.has(line.trim().toLowerCase()),
    );

    if (missingFooterLines.length === 0) {
      return dedupedContent;
    }

    return `${dedupedContent}\n\n${missingFooterLines.join("\n")}`.trim();
  }

  private static trimToMaxChars(value: string, max: number): string {
    const normalized = String(value || "").trim();
    if (normalized.length <= max) return normalized;
    if (max <= 3) {
      return normalized.slice(0, max).trimEnd();
    }
    return `${normalized.slice(0, Math.max(0, max - 3)).trimEnd()}...`;
  }
}
